import { computed, inject, Injectable } from '@angular/core';
import { BankAccountsStore } from './bank-accounts.store';
import { AuthStore } from './auth.store';
import { BankAccountStatus, CustomerStatus } from './types';
import { getAccountOwnerName } from '../utils/utils';

/**
 * Store para manejar los permisos de acceso a cuentas autorizadas
 * Proporciona métodos reutilizables para validar permisos en toda la aplicación
 */
@Injectable({ providedIn: 'root' })
export class AccountAccessStore {
  private bankAccountsStore = inject(BankAccountsStore);
  private authStore = inject(AuthStore);

  /**
   * Verifica si el customer está inactivo
   */
  isCustomerInactive = computed(() => {
    const customer = this.authStore.customer();
    return customer?.status === CustomerStatus.INACTIVE;
  });

  /**
   * Verifica si la cuenta seleccionada actualmente es una cuenta autorizada
   */
  isAuthorizedAccount = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    return selectedAccount?.ownAccount === false && selectedAccount?.authorizedAccount === true;
  });

  /**
   * Verifica si la cuenta seleccionada es de solo lectura (view_only)
   */
  isViewOnlyAccount = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    if (!selectedAccount?.access?.permission) return false;
    return selectedAccount.access.permission === 'view_only';
  });

  /**
   * Verifica si la cuenta seleccionada está suspendida
   */
  isSelectedAccountSuspended = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    return selectedAccount?.status === BankAccountStatus.SUSPENDED;
  });

  /**
   * Verifica si la cuenta seleccionada tiene acceso completo
   */
  isFullAccessAccount = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    if (!selectedAccount?.access?.permission) return true; // Cuentas propias tienen acceso completo
    return selectedAccount.access.permission === 'full_access';
  });

  /**
   * Verifica si el usuario puede realizar transferencias con la cuenta seleccionada
   */
  canMakeTransfers = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    
    // Si el customer está inactivo, no puede hacer transferencias
    if (this.isCustomerInactive()) return false;
    
    // Si la cuenta está suspendida, no puede hacer transferencias
    if (selectedAccount?.status === BankAccountStatus.SUSPENDED) return false;
    
    // Si es cuenta propia, puede hacer transferencias
    if (selectedAccount?.ownAccount !== false) return true;
    
    // Si es cuenta autorizada, verificar permisos
    if (selectedAccount?.access?.permission === 'view_only') return false;
    if (selectedAccount?.access?.permission === 'full_access') return true;
    
    // Por defecto, verificar si tiene la acción permitida
    if (selectedAccount?.access?.actionsAllowed?.includes('transfer')) return true;
    
    return false;
  });

  /**
   * Verifica si el usuario puede agregar beneficiarios con la cuenta seleccionada
   */
  canAddBeneficiaries = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    
    // Si el customer está inactivo, no puede agregar beneficiarios
    if (this.isCustomerInactive()) return false;
    
    // Si es cuenta propia, puede agregar beneficiarios
    if (selectedAccount?.ownAccount !== false) return true;
    
    // Si es cuenta autorizada, verificar permisos
    if (selectedAccount?.access?.permission === 'view_only') return false;
    if (selectedAccount?.access?.permission === 'full_access') return true;
    
    // Por defecto, verificar si tiene la acción permitida
    if (selectedAccount?.access?.actionsAllowed?.includes('add_beneficiary')) return true;
    
    return false;
  });

  /**
   * Verifica si el usuario puede crear solicitudes de pago con la cuenta seleccionada
   */
  canCreatePaymentRequests = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    
    // Si el customer está inactivo, no puede crear solicitudes de pago
    if (this.isCustomerInactive()) return false;
    
    // Si la cuenta está suspendida, no puede crear solicitudes de pago
    if (selectedAccount?.status === BankAccountStatus.SUSPENDED) return false;
    
    // Si es cuenta propia, puede crear solicitudes
    if (selectedAccount?.ownAccount !== false) return true;
    
    // Si es cuenta autorizada, verificar permisos
    if (selectedAccount?.access?.permission === 'view_only') return false;
    if (selectedAccount?.access?.permission === 'full_access') return true;
    
    // Por defecto, verificar si tiene la acción permitida
    if (selectedAccount?.access?.actionsAllowed?.includes('create_payment_request')) return true;
    
    return false;
  });

  /**
   * Verifica si el usuario solo puede ver la información de la cuenta
   */
  canOnlyView = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    
    // Si es cuenta propia, tiene acceso completo
    if (selectedAccount?.ownAccount !== false) return false;
    
    // Si es cuenta autorizada con view_only
    return selectedAccount?.access?.permission === 'view_only';
  });

  /**
   * Obtiene el nombre del propietario de la cuenta autorizada
   */
  authorizedAccountOwner = computed(() => {
    return getAccountOwnerName(this.bankAccountsStore.selectedBankAccount());
  });

  /**
   * Obtiene el tipo de permiso de la cuenta seleccionada
   */
  currentPermission = computed(() => {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    return selectedAccount?.access?.permission || 'full_access';
  });

  /**
   * Verifica si una acción específica está permitida en la cuenta seleccionada
   */
  canPerformAction(action: string): boolean {
    const selectedAccount = this.bankAccountsStore.selectedBankAccount();
    
    // Si es cuenta propia, todas las acciones están permitidas
    if (selectedAccount?.ownAccount !== false) return true;
    
    // Si es cuenta autorizada con full_access, todas las acciones están permitidas
    if (selectedAccount?.access?.permission === 'full_access') return true;
    
    // Si es view_only, ninguna acción está permitida
    if (selectedAccount?.access?.permission === 'view_only') return false;
    
    // Verificar en acciones permitidas
    return selectedAccount?.access?.actionsAllowed?.includes(action) || false;
  }

  /**
   * Obtiene un mensaje de error personalizado según el permiso
   */
  getAccessDeniedMessage(): string {
    if (this.isCustomerInactive()) {
      return 'Su cuenta de cliente está inactiva. No puede realizar esta acción.';
    }
    if (this.isSelectedAccountSuspended()) {
      return 'La cuenta seleccionada está suspendida. No puede realizar esta acción.';
    }
    if (this.isViewOnlyAccount()) {
      const owner = this.authorizedAccountOwner();
      return `Esta es una cuenta autorizada de ${owner || 'otro usuario'} con permisos de solo lectura. No puede realizar esta acción.`;
    }
    return 'No tiene permisos suficientes para realizar esta acción en la cuenta seleccionada.';
  }
}
